import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import { sanitizeRichText } from "../../../../../../../../common/utils/richText";
import RichTextEditor from "../../../../../../../../common/components/RichTextEditor";

export type AddBenefitFormValues = {
  name: string;
  description: string;
  benefitsType: string;
  benefitsStatus: number;
  deliveryType: string;
  weightage: number | null;
  frequency: string;
};

type Props = {
  open: boolean;
  mode?: "add" | "edit";
  initialValues?: Partial<AddBenefitFormValues> | null;
  onClose: () => void;
  onSubmit: (values: AddBenefitFormValues) => void;
};

const BENEFIT_TYPE_OPTIONS = [
  { label: "Marketing", value: "Marketing" },
  { label: "Delivery", value: "Delivery" },
];

const DELIVERY_TYPE_OPTIONS = [
  { label: "Recurring", value: "Recurring" },
  { label: "One-time", value: "One-time" },
  { label: "On-demand", value: "On-demand" },
];

const WEIGHTAGE_OPTIONS = [5, 10, 15, 20];

const FREQUENCY_OPTIONS = [
  { label: "Yearly", value: "Yearly" },
  { label: "Half Yearly", value: "Half Yearly" },
  { label: "Quarterly", value: "Quarterly" },
  { label: "Monthly", value: "Monthly" },
];

const TabPanel: React.FC<{
  index: number;
  value: number;
  children: React.ReactNode;
}> = ({ index, value, children }) => {
  if (value !== index) return null;
  return <Box className="benefits-add-tab-panel">{children}</Box>;
};

const RequiredLabel: React.FC<{ htmlFor?: string; children: React.ReactNode }> = ({
  htmlFor,
  children,
}) => (
  <label className="benefits-ptv-modal-label" htmlFor={htmlFor}>
    {children}
    <span className="benefits-required-asterisk">*</span>
  </label>
);

const initialFormState = {
  name: "",
  description: "",
  benefitsType: "",
  benefitsStatus: 1,
  deliveryType: "",
  weightage: "" as string | number,
  frequency: "",
};

const AddBenefitModal: React.FC<Props> = ({
  open,
  mode = "add",
  initialValues = null,
  onClose,
  onSubmit,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState(initialFormState);
  const descriptionTextLength = form.description.replace(/<[^>]*>/g, "").trim().length;
  const showDeliveryFields = form.benefitsType === "Delivery";
  const showFrequency = form.deliveryType === "Recurring";
  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setForm(initialFormState);
      return;
    }

    setActiveTab(0);
    if (mode === "edit" && initialValues) {
      setForm({
        name: initialValues.name || "",
        description: initialValues.description || "",
        benefitsType: initialValues.benefitsType || "",
        benefitsStatus:
          initialValues.benefitsStatus === 0 || initialValues.benefitsStatus === 1
            ? initialValues.benefitsStatus
            : 1,
        deliveryType: initialValues.deliveryType || "",
        weightage:
          initialValues.weightage === null || initialValues.weightage === undefined
            ? ""
            : initialValues.weightage,
        frequency: initialValues.frequency || "",
      });
      return;
    }

    setForm(initialFormState);
    // Hydrate only when modal opens / mode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const isFormValid = useMemo(() => {
    if (!form.name.trim() || !form.benefitsType) return false;
    if (form.benefitsType === "Delivery") {
      if (!form.deliveryType || form.weightage === "") return false;
      if (form.deliveryType === "Recurring" && !form.frequency) return false;
    }
    return true;
  }, [form]);

  const handleSubmit = () => {
    if (!isFormValid) return;
    const isDelivery = form.benefitsType === "Delivery";
    onSubmit({
      name: form.name.trim(),
      description: sanitizeRichText(form.description),
      benefitsType: form.benefitsType,
      benefitsStatus: form.benefitsStatus,
      deliveryType: isDelivery ? form.deliveryType : "",
      weightage: isDelivery
        ? form.weightage === ""
          ? null
          : Number(form.weightage)
        : null,
      frequency:
        isDelivery && form.deliveryType === "Recurring" ? form.frequency : "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "6px",
          boxShadow: "0px 12px 32px rgba(15, 23, 42, 0.18)",
          maxWidth: 640,
        },
      }}
    >
      <div className="benefits-ptv-modal">
        <header className="benefits-ptv-modal-header">
          <div>
            <h3>{isEditMode ? "Edit Benefit" : "Add New Benefit"}</h3>
            <p className="benefits-ptv-modal-subtitle">
              {isEditMode
                ? "Update benefit details with partner tier."
                : "Configure a new benefit with partner tier."}
            </p>
          </div>
          <button
            type="button"
            className="benefits-ptv-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <Iconography icon="xmark" size={IconographySizes.Medium} />
          </button>
        </header>

        <DialogContent className="benefits-ptv-modal-content">
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            aria-label="Benefit tabs"
            className="benefits-add-tabs"
          >
            <Tab label="Service Details" />
            <Tab label="Description" />
          </Tabs>

          <TabPanel index={0} value={activeTab}>
            <div className="benefits-add-top-row">
              <div className="benefits-add-field">
                <RequiredLabel htmlFor="benefit-name">Benefit Name</RequiredLabel>
                <TextField
                  id="benefit-name"
                  value={form.name}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                  fullWidth
                  placeholder="e.g., Technical Excellence"
                  size="small"
                />
              </div>
              <div className="benefits-add-field benefits-add-status-field">
                <RequiredLabel>Benefit Status</RequiredLabel>
                <FormControlLabel
                  className="benefits-status-toggle"
                  control={
                    <Switch
                      checked={form.benefitsStatus === 1}
                      onChange={(_, checked) =>
                        setForm((prev) => ({
                          ...prev,
                          benefitsStatus: checked ? 1 : 0,
                        }))
                      }
                      color="success"
                    />
                  }
                  label={form.benefitsStatus === 1 ? "Active" : "Inactive"}
                />
              </div>
            </div>

            <div className="benefits-add-field">
              <RequiredLabel htmlFor="benefit-type">Benefit Type</RequiredLabel>
              <TextField
                id="benefit-type"
                select
                fullWidth
                size="small"
                value={form.benefitsType}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    benefitsType: event.target.value,
                    deliveryType: "",
                    weightage: "",
                    frequency: "",
                  }))
                }
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                {BENEFIT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            {showDeliveryFields ? (
              <>
                <div className="benefits-add-divider" />
                <div
                  className={`benefits-add-delivery-row${
                    showFrequency ? "" : " two-cols"
                  }`}
                >
                  <div className="benefits-add-field">
                    <RequiredLabel htmlFor="delivery-type">Delivery Type</RequiredLabel>
                    <TextField
                      id="delivery-type"
                      select
                      fullWidth
                      size="small"
                      value={form.deliveryType}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          deliveryType: event.target.value,
                          frequency:
                            event.target.value === "Recurring" ? prev.frequency : "",
                        }))
                      }
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>
                      {DELIVERY_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  <div className="benefits-add-field">
                    <RequiredLabel htmlFor="weightage">Weightage</RequiredLabel>
                    <TextField
                      id="weightage"
                      select
                      fullWidth
                      size="small"
                      value={form.weightage}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          weightage: event.target.value,
                        }))
                      }
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>
                      {WEIGHTAGE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  {showFrequency ? (
                    <div className="benefits-add-field">
                      <RequiredLabel htmlFor="frequency">Frequency</RequiredLabel>
                      <TextField
                        id="frequency"
                        select
                        fullWidth
                        size="small"
                        value={form.frequency}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            frequency: event.target.value,
                          }))
                        }
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="" disabled>
                          Select
                        </MenuItem>
                        {FREQUENCY_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </TabPanel>

          <TabPanel index={1} value={activeTab}>
            <div className="benefits-ptv-modal-textarea-header">
              <label className="benefits-ptv-modal-label" htmlFor="benefit-description">
                Description
              </label>
              <span>{descriptionTextLength}/800</span>
            </div>
            <div id="benefit-description" className="benefits-inline-editor-wrapper">
              <RichTextEditor
                id="benefit-description-editor"
                placeholder="Add a short benefit description"
                value={form.description}
                onChange={(value: string) =>
                  setForm((prev) => ({ ...prev, description: value }))
                }
              />
            </div>
          </TabPanel>
        </DialogContent>

        <footer className="benefits-ptv-modal-footer">
          <Button variant="secondary" label="CANCEL" onClick={onClose} />
          <Button
            variant="primary"
            label={isEditMode ? "UPDATE BENEFIT" : "ADD BENEFIT"}
            onClick={handleSubmit}
            disabled={!isFormValid}
          />
        </footer>
      </div>
    </Dialog>
  );
};

export default AddBenefitModal;
